/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { Grid, Card, Button, Icon } from '@scuf/common';
import React, { useState } from 'react';
import './DataUploadCardsTheme.scss';
import { useFileUploadContext } from '../../../context/FileUploadContext/FileUploadContext';
import FileUploadAction from '../../../context/FileUploadContext/FileUploadActions';
import ZLOUploadModal from '../../ZLOOnboardingConfigSettings/UploadModal/UploadModal';

import IEnergyManagementProps from '../../DashboardKPIWidgets/IEnergyManagementProps';
import DataUploadComponent from './DataUploadComponent';
import descriptors from '../../../locales/en-US/descriptors';
import { useTranslation } from '@scuf/localization';
import UpdatedDataUploadComponent from '../MeterDataUpload/UpdatedDataUploadComponent';

const DataUploadCards = (props: IEnergyManagementProps) => {
  const d = descriptors.energyManagement;
  const z = descriptors.ZLOOnboardingConfig;
  const [t] = useTranslation();
  const [state, dispatch] = useFileUploadContext();
  const showZLOUploadModal = () =>
    dispatch(FileUploadAction.showZLOUploadModal());

  const [dashboardName, setDashboardName] = useState('');

  const {
    bearerToken,
    requestContext,
    toggleFeatureScopeOne,
    toggleFeatureScopeTwo,
    toggleFeatureAllUpload,
    connectorConfig,
    emaasContext,
    pointHistoryUploadToggle,
    toggleFeatureGAS
  } = props;
  const enableZLODataUpload = emaasContext && emaasContext.enableZLODataUpload;

  const renderDashboard = (name: string) => {
    switch (name) {
      case 'CarbonEmission':
        return (
          <DataUploadComponent
            connectorConfig={connectorConfig}
            requestContext={requestContext}
            bearerToken={bearerToken}
            emaasContext={emaasContext}
            toggleFeatureScopeOne={toggleFeatureScopeOne}
            toggleFeatureScopeTwo={toggleFeatureScopeTwo}
            toggleFeatureAllUpload={toggleFeatureAllUpload}
            toggleFeatureGAS={toggleFeatureGAS}
          />
        );
      case 'MeterDataUpload':
        return (
          <UpdatedDataUploadComponent
            connectorConfig={connectorConfig}
            requestContext={requestContext}
            bearerToken={bearerToken}
            emaasContext={emaasContext}
            toggleFeatureAllUpload={toggleFeatureAllUpload}
            pointHistoryUploadToggle={pointHistoryUploadToggle}
            toggleFeatureGAS={toggleFeatureGAS}
          />
        );
      default:
        return (
          <>
            <div className="header-wrapper">
              <div className="data-upload-cards-setting-header">
                <div className="data-upload-cards-setting-title">
                  {t(d.DATAUPLOAD)}
                </div>
              </div>
              <div className="data-upload-cards-setting-content">
                <span>{t(d.DATAUPLOADDETAILS)}</span>
              </div>
            </div>
            <div className="grid-wrapper">
              <Grid className="layout-grid">
                <Grid.Row className="layout-content-grid-row">
                  <Grid.Column width={6} xsWidth={12}>
                    <Card className="data-upload-card">
                      <Card.Header>
                        <div>{t(d.CARBONEMISSION)}</div>
                      </Card.Header>

                      <Card.Content className="data-upload-card-content">
                        <div className="card-subtitle">
                          {t(d.DATAUPLOADCECARDSUBTITLE)}
                        </div>
                        <div className="carbon-emission-upload-button">
                          <Button
                            onClick={() => {
                              setDashboardName('CarbonEmission');
                            }}
                            type="secondary"
                            content={t(d.VIEWDETAILS)}
                          />
                        </div>
                      </Card.Content>
                    </Card>
                  </Grid.Column>
                  {enableZLODataUpload && (
                    <Grid.Column width={6} xsWidth={12}>
                      <Card className="data-upload-card">
                        <Card.Header>
                          <div>{t(z.UPLOAD_CARD_TITLE_ZLO)}</div>
                        </Card.Header>

                        <Card.Content className="data-upload-card-content">
                          <div className="card-subtitle">
                            {t(z.UPLOAD_CARD_DESC_ZLO)}
                          </div>
                          <div>
                            <Button
                              onClick={showZLOUploadModal}
                              type="secondary"
                              content="Get Started"
                            />
                          </div>
                        </Card.Content>
                      </Card>
                    </Grid.Column>
                  )}
                  {pointHistoryUploadToggle && (
                    <Grid.Column width={6} xsWidth={12}>
                      <Card className="data-upload-card">
                        <Card.Header>
                          <div>{t(d.METERDATA)}</div>
                        </Card.Header>
                        <Card.Content className="data-upload-card-content">
                          <div className="card-subtitle">
                            {t(d.METERDATASUBTITLE)}
                          </div>
                          <div className="meter-data-upload-button">
                            <Button
                              onClick={() => {
                                setDashboardName('MeterDataUpload');
                              }}
                              type="secondary"
                              content={t(d.VIEWDETAILS)}
                            />
                          </div>
                        </Card.Content>
                      </Card>
                    </Grid.Column>
                  )}
                </Grid.Row>
              </Grid>
            </div>
          </>
        );
    }
  };

  return (
    <div className="data-upload-cards-page ">
      <div className="data-upload-cards-setting-section">
        {dashboardName !== '' && (
          <div className="data-upload-cards-back-button">
            <Icon
              root="common"
              name="arrow-left"
              size="medium"
              onClick={() => setDashboardName('')}
            />
          </div>
        )}
        {renderDashboard(dashboardName)}
        <ZLOUploadModal />
      </div>
    </div>
  );
};

export default DataUploadCards;
